import { ArdoqId } from '@ardoq/api-types';
import { isGroup } from './util';
import {
  BlocksGraph,
  BlocksReference,
  BlocksNode,
  BlocksVisualState,
  AqBlocks,
  ItemSet,
} from './types';

export type GraphMerge = {
  nodesAdded: number;
  nodesDeleted: number;
  nodesReused: number;

  referencesAdded: number;
  referencesDeleted: number;
  referencesReused: number;
};

/**
 * Every group's children in `modelId` order, in place.
 *
 * Exported because it is not bookkeeping: `fixupLocalGridLayout` assigns cells by array index,
 * so on the `grid` layout a permutation of `children` is a different picture, and the global
 * layouts inherit it as yFiles' tie-breaking order. Anything laying a graph out without going
 * through `mergeGraph` has to apply this first or it is laying out a different graph.
 */
export const sortGraphChildren = (graph: BlocksGraph) => {
  const compareFn = (a: BlocksNode, b: BlocksNode) => {
    if (!a.modelId || !b.modelId) return 0;
    if (a.modelId < b.modelId) return -1;
    if (a.modelId > b.modelId) return 1;
    return 0;
  };

  const doit = (node: BlocksNode) => {
    if (node.children) {
      for (const child of node.children) {
        doit(child);
      }

      if (node.children.length > 1) {
        node.children.sort(compareFn);
      }
    }
  };

  if (graph.root) {
    doit(graph.root);
  }
};

const createStableReferenceMap = (
  graph: BlocksGraph | null
): Map<string, BlocksReference> => {
  const ret = new Map<ArdoqId, BlocksReference>();
  const counts = new Map<ArdoqId, number>();

  if (graph) {
    for (const reference of graph.edges) {
      const unstableId = reference.modelId;

      if (unstableId) {
        const count = counts.get(unstableId) ?? 0;
        counts.set(unstableId, count + 1);

        const stableId = `${unstableId}/${count}`;
        ret.set(stableId, reference);
      }
    }
  }

  return ret;
};

const createStableNodeMap = (graph: BlocksGraph | null) => {
  const ret = new Map<ArdoqId, BlocksNode>();
  const counts = new Map<ArdoqId, number>();

  if (!graph) {
    return ret;
  }

  const doit = (node: BlocksNode) => {
    const unstableId = node.modelId;

    if (unstableId) {
      const count = counts.get(unstableId) ?? 0;
      counts.set(unstableId, count + 1);

      const stableId = `${unstableId}/${count}`;
      ret.set(stableId, node);
    }

    if (node.children) {
      for (const child of node.children) {
        doit(child);
      }
    }
  };

  doit(graph.root);

  return ret;
};

const fixupSet = (
  itemSet: ItemSet,
  newItem: BlocksNode | BlocksReference | undefined,
  oldItem: BlocksNode | BlocksReference | undefined
) => {
  if (oldItem && itemSet.has(oldItem)) {
    itemSet.delete(oldItem);

    if (newItem) {
      itemSet.add(newItem);
    }
  }
};

export const mergeGraph = (
  component: AqBlocks,
  graph: BlocksGraph | null,
  oldGraph: BlocksGraph | null
): GraphMerge => {
  if (graph) {
    sortGraphChildren(graph);
  }

  const oldNodes = createStableNodeMap(oldGraph);
  const newNodes = createStableNodeMap(graph);

  const ret: GraphMerge = {
    nodesAdded: 0,
    nodesDeleted: 0,
    nodesReused: 0,

    referencesAdded: 0,
    referencesDeleted: 0,
    referencesReused: 0,
  };

  for (const [newNodeId, newNode] of newNodes) {
    const oldNode = oldNodes.get(newNodeId);

    if (oldNode) {
      /* reused */
      ret.nodesReused++;

      /* a better way to do this will be found */

      //# region not sure about these ones

      newNode.displayLabels = oldNode.displayLabels; // will be set by layout
      if (isGroup(newNode) === isGroup(oldNode)) {
        newNode.layoutType = oldNode.layoutType;
      }

      newNode.layoutData = undefined;
      if (newNode.parent) {
        newNode.parent.isLayoutDirty = true;
      }

      //#endregion

      // #region  new node doesn't have all this derived stuff yet

      newNode.width = oldNode.width; // item preferred width (comes off node style; ignored for open group items)
      newNode.height = oldNode.height; // item preferred height (comes off node style; ignored for open group items) */
      newNode.isLayoutDirty = oldNode.isLayoutDirty;
      newNode.cell = oldNode.cell; // world cell rectangle
      newNode.bounds = oldNode.bounds; // world bounds rectangle
      newNode.content = oldNode.content; // open group child area
      newNode.rowSize = oldNode.rowSize; // height of group node rows;
      newNode.colSize = oldNode.colSize; // width of group node columns;
      newNode.visualState = oldNode.visualState;
      // #endregion

      const oldParent = oldNode.parent;
      const newParent = newNode.parent;

      if (oldParent /* && oldParent.modelId !== newParent?.modelId*/) {
        oldParent.isLayoutDirty = true;
      }

      if (newParent /* && oldParent?.modelId !== newParent.modelId*/) {
        newParent.isLayoutDirty = true;
      }

      fixupSet(component.selected, newNode, oldNode);
      fixupSet(component.highlighted, newNode, oldNode);
      fixupSet(component.hovered, newNode, oldNode);
    } else {
      /* added */
      ret.nodesAdded++;

      newNode.visualState = BlocksVisualState.None;

      if (newNode.parent) {
        /* oldNode's parent's children have changed */
        newNode.parent.isLayoutDirty = true;
      }
    }
  }

  for (const [oldNodeId, oldNode] of oldNodes) {
    const newNode = newNodes.get(oldNodeId);

    if (!newNode) {
      /* deleted */
      ret.nodesDeleted++;

      if (oldNode.parent) {
        /* oldNode's parent's children have changed */
        oldNode.parent.isLayoutDirty = true;
      }

      fixupSet(component.selected, undefined, oldNode);
      fixupSet(component.highlighted, undefined, oldNode);
      fixupSet(component.hovered, undefined, oldNode);
    }
  }

  const oldReferences = createStableReferenceMap(oldGraph);
  const newReferences = createStableReferenceMap(graph);

  for (const [newReferenceId, newReference] of newReferences) {
    const oldReference = oldReferences.get(newReferenceId);

    if (oldReference) {
      /* reused */
      ret.referencesReused++;

      newReference.route = oldReference.route;
      newReference.bounds = oldReference.bounds;
      newReference.visualState = oldReference.visualState;

      fixupSet(component.selected, newReference, oldReference);
      fixupSet(component.highlighted, newReference, oldReference);
      fixupSet(component.hovered, newReference, oldReference);
    } else {
      /* added */
      ret.referencesAdded++;

      newReference.visualState = BlocksVisualState.None;
    }
  }

  for (const [oldReferenceId, oldReference] of oldReferences) {
    const newReference = newReferences.get(oldReferenceId);

    if (!newReference) {
      ret.referencesDeleted++;

      fixupSet(component.selected, undefined, oldReference);
      fixupSet(component.highlighted, undefined, oldReference);
      fixupSet(component.hovered, undefined, oldReference);
    }
  }

  return ret;
};
